import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface RouteGuardProps {
  requiredGroups?: string[];
  children: React.ReactElement;
}

const RouteGuard: React.FC<RouteGuardProps> = ({ requiredGroups, children }) => {
  const { isLoading, hasAnyGroup, groups } = useAuth();
  const location = useLocation();
  const [hasCheckedGroups, setHasCheckedGroups] = useState(false);
  const [isGroupsReady, setIsGroupsReady] = useState(false);

  useEffect(() => {
    // Once loading is complete AND we have groups (or no groups are required), mark as ready
    if (!isLoading) {
      // If no groups are required, or we've received groups (even if empty array), we're ready
      const ready = !requiredGroups || requiredGroups.length === 0 || hasCheckedGroups;
      setIsGroupsReady(ready);

      if (!hasCheckedGroups) {
        // Give a small delay to ensure groups are extracted
        setTimeout(() => {
          setHasCheckedGroups(true);
          setIsGroupsReady(true);
          // Only log in development without exposing group IDs
          if (process.env.NODE_ENV === 'development') {
            console.log('[RouteGuard] Auth check completed');
          }
        }, 100);
      }
    }
  }, [isLoading, groups, requiredGroups, hasCheckedGroups]);

  // Show loading spinner while auth is loading or groups aren't ready
  if (isLoading || !isGroupsReady) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="spinner" />
      </div>
    );
  }

  // Check permissions only after we're sure auth and groups are loaded
  const hasPermission = hasAnyGroup(requiredGroups);

  if (!hasPermission) {
    // Only log in development without exposing sensitive data
    if (process.env.NODE_ENV === 'development') {
      console.log('[RouteGuard] Access denied for path:', location.pathname);
    }
    return <Navigate to="/forbidden" replace state={{ from: location.pathname }} />;
  }

  return children;
};

export default RouteGuard;
